// Resume list table per the dashboard mock: CANDIDATE / FILE / UPLOADED /
// ADDED BY / OWNER / STATUS columns and a per-row overflow menu
// (Open / Delete). Shared by the dashboard's "Recent resumes" card and the
// Resumes page. The OWNER cell assigns the candidate owner in place.

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileText, MoreHorizontal, Trash2, ExternalLink, UserPlus } from "lucide-react";
import { STATUS_LABEL, displayNameFromEmail, relativeTime, type JobListItem } from "@/lib/jobs";
import { useOwnerOptions } from "@/hooks/useOwnerOptions";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Avatar } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { OwnerCell } from "@/components/OwnerCell";

function RowMenu({
  job,
  onDelete,
  onAssignOwner,
}: {
  job: JobListItem;
  onDelete: (job: JobListItem) => void;
  onAssignOwner: (job: JobListItem) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        aria-label="Row actions"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "inline-flex h-8 w-8 items-center justify-center rounded-md text-text-2 hover:bg-surface-2 hover:text-text",
          open && "bg-surface-2 text-text",
        )}
      >
        <MoreHorizontal size={16} strokeWidth={1.75} />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-40 mt-1 w-40 animate-fade-in overflow-hidden rounded-md border border-border bg-surface py-1.5 shadow-pop"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              navigate(`/jobs/${job.id}`);
            }}
            className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm text-text hover:bg-surface-2"
          >
            <ExternalLink className="h-4 w-4 text-text-2" strokeWidth={1.75} aria-hidden />
            Open
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onAssignOwner(job);
            }}
            className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm text-text hover:bg-surface-2"
          >
            <UserPlus className="h-4 w-4 text-text-2" strokeWidth={1.75} aria-hidden />
            {job.candidateOwner ? "Change owner" : "Assign owner"}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onDelete(job);
            }}
            className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm text-danger-on hover:bg-surface-2"
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.75} aria-hidden />
            Delete
          </button>
        </div>
      ) : null}
    </div>
  );
}

const TH_CLASS =
  "whitespace-nowrap px-5 py-2.5 text-left text-[11px] font-medium uppercase tracking-[0.14em] text-text-2";

export function ResumeTable({
  jobs,
  loading = false,
  onDelete,
}: {
  jobs: JobListItem[];
  loading?: boolean;
  onDelete: (id: string) => void;
}) {
  // Row deletes confirm here, once, so every list gets the same dialog.
  const [pendingDelete, setPendingDelete] = useState<JobListItem | null>(null);
  // Row whose Owner cell is in edit mode (entered via the "…" menu).
  const [ownerEditId, setOwnerEditId] = useState<string | null>(null);

  const { options: ownerOptions, addOwner } = useOwnerOptions();
  return (
    <div className="overflow-x-auto xl:overflow-x-visible">
      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete this resume?"
        body={
          pendingDelete ? (
            <>
              <span className="font-semibold text-text">
                {pendingDelete.candidateName ?? pendingDelete.sourceFilename}
              </span>{" "}
              and every generated document for it are removed. This cannot be undone.
            </>
          ) : null
        }
        confirmLabel="Delete"
        danger
        onConfirm={() => {
          if (pendingDelete) onDelete(pendingDelete.id);
          setPendingDelete(null);
        }}
        onCancel={() => setPendingDelete(null)}
      />
      {/* Fixed layout so the table can never outgrow the card: narrow columns
          are pinned, Candidate/File share the rest and truncate. Below xl the
          wrapper scrolls instead (the sidebar leaves too little room). */}
      <table className="w-full min-w-[880px] table-fixed border-collapse text-[14px] xl:min-w-0">
        <thead>
          <tr className="border-b border-border">
            <th className={TH_CLASS}>Candidate</th>
            <th className={TH_CLASS}>File</th>
            <th className={cn(TH_CLASS, "w-28")}>Uploaded</th>
            <th className={cn(TH_CLASS, "w-28")}>Added by</th>
            <th className={cn(TH_CLASS, "w-40")}>Owner</th>
            <th className={cn(TH_CLASS, "w-40")}>Status</th>
            <th className={cn(TH_CLASS, "w-12")} aria-label="Actions" />
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <tr key={i}>
                  <td className="px-5 py-3.5">
                    <span className="flex items-center gap-3">
                      <Skeleton className="h-9 w-9 rounded-full" />
                      <Skeleton className="h-3.5 w-32" />
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <Skeleton className="h-3.5 w-44" />
                  </td>
                  <td className="px-5 py-3.5">
                    <Skeleton className="h-3.5 w-14" />
                  </td>
                  <td className="px-5 py-3.5">
                    <Skeleton className="h-3.5 w-16" />
                  </td>
                  <td className="px-5 py-3.5">
                    <Skeleton className="h-3.5 w-20" />
                  </td>
                  <td className="px-5 py-3.5">
                    <Skeleton className="h-6 w-28 rounded-full" />
                  </td>
                  <td className="px-5 py-3.5" />
                </tr>
              ))
            : jobs.map((job) => {
                const status = STATUS_LABEL[job.status];
                return (
                  <tr key={job.id} className="group transition-colors hover:bg-surface-2">
                    <td className="px-5 py-3">
                      <Link
                        to={`/jobs/${job.id}`}
                        className="flex min-w-0 items-center gap-3 no-underline"
                      >
                        {job.candidateName ? (
                          <Avatar alt={job.candidateName} size="sm" />
                        ) : (
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-2 text-text-2">
                            <FileText size={14} strokeWidth={1.75} />
                          </span>
                        )}
                        <span className="truncate font-semibold text-text">
                          {job.candidateName ?? "—"}
                        </span>
                      </Link>
                    </td>
                    <td className="px-5 py-3">
                      <Link to={`/jobs/${job.id}`} className="flex items-center gap-2 no-underline">
                        <FileText
                          size={15}
                          strokeWidth={1.75}
                          className="shrink-0 text-brand-on"
                          aria-hidden
                        />
                        <span className="min-w-0">
                          <span className="block truncate text-text">
                            {job.sourceFilename}
                            {job.sourceCount > 1 && (
                              <span className="text-text-2"> +{job.sourceCount - 1} more</span>
                            )}
                          </span>
                          {job.outputCount > 0 && (
                            <span className="block text-[12px] text-text-2">
                              {job.outputCount} document{job.outputCount === 1 ? "" : "s"}
                            </span>
                          )}
                        </span>
                      </Link>
                    </td>
                    <td className="whitespace-nowrap px-5 py-3 text-text-2">
                      {relativeTime(job.createdAt)}
                    </td>
                    <td className="truncate px-5 py-3 text-text-2">
                      {displayNameFromEmail(job.ownerEmail) ?? job.ownerEmail}
                    </td>
                    <td className="whitespace-nowrap px-5 py-3">
                      <OwnerCell
                        value={job.candidateOwner}
                        patchPath={`/api/resume-jobs/${job.id}/candidate-owner`}
                        invalidateKey="jobs"
                        options={ownerOptions}
                        onCreate={addOwner}
                        editing={ownerEditId === job.id}
                        onClose={() => setOwnerEditId(null)}
                      />
                    </td>
                    <td className="whitespace-nowrap px-5 py-3">
                      <Badge variant={status.variant}>{status.label}</Badge>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <RowMenu
                        job={job}
                        onDelete={setPendingDelete}
                        onAssignOwner={(j) => setOwnerEditId(j.id)}
                      />
                    </td>
                  </tr>
                );
              })}
        </tbody>
      </table>
    </div>
  );
}
